import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { BookOpen, Plus, Trash, UploadSimple } from '@phosphor-icons/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getAPIErrorMessage } from '../../helpers/helpers';
import { FlowService } from '../../service/FlowService';
import { LibraryService } from '../../service/LibraryService';
import { UserService } from '../../service/UserService';
import { useAuthStore } from '../../store/storeAuth';
import { useNotificationStore } from '../../store/storeNotifications';
import KitModal from '../UI/KitModal';

type Props = {
  onHide: () => void;
  show: boolean;
  flow: any;
  folderId?: string;
  folder?: any;
  spaceId?: string;
  onAddToLibrary: (data: {
    libraryType: string;
    image?: File;
  }) => void;
  isEditMode?: boolean;
};

const AddToLibraryModal = ({
  onHide,
  show,
  flow,
  folderId,
  folder,
  spaceId,
  onAddToLibrary,
  isEditMode = false,
}: Props) => {
  const { user, folders } = useAuthStore(state => state);
  const { setNotification } = useNotificationStore(state => state);

  const getInitialLibraryType = () => {
    if (isEditMode && flow?.library) {
      const type = flow.library.type;
      if (type === 'system') return 'global';
      if (type === 'organization') return 'environment';
      if (type === 'space' || type === 'private') return 'space';
    }
    return 'space';
  };

  const [libraryType, setLibraryType] = useState(getInitialLibraryType());
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(
    isEditMode && flow?.library?.image_url ? flow.library.image_url : null
  );
  const [dragActive, setDragActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isProductAdmin, setIsProductAdmin] = useState(false);
  const [inputNodes, setInputNodes] = useState<any[]>([]);
  const [outputNodes, setOutputNodes] = useState<any[]>([]);
  const [visibleInputIds, setVisibleInputIds] = useState<string[]>([]);
  const [visibleOutputIds, setVisibleOutputIds] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const checkProductAdmin = async () => {
      if (!show) return;

      try {
        const response = await UserService.isProductAdmin();
        const productAdminStatus = response?.data?.isProductAdmin ?? false;
        setIsProductAdmin(productAdminStatus);
      } catch (error: any) {
        setIsProductAdmin(false);
      }
    };

    checkProductAdmin();
  }, [show]);

  useEffect(() => {
    if (!isProductAdmin && libraryType === 'global') {
      setLibraryType('space');
    }
    if (!user?.selectedCompanyId && libraryType === 'environment') {
      setLibraryType('space');
    }
  }, [isProductAdmin, libraryType, user?.selectedCompanyId]);

  useEffect(() => {
    const fetchFlowNodes = async () => {
      if (show && flow?.id) {
        try {
          const response = await FlowService.getFlow(flow.id);
          const fullFlow = response?.data;

          if (fullFlow?.nodes) {
            const inputs = fullFlow.nodes.filter((node: any) => node.type === 'varInputNode');
            const outputs = fullFlow.nodes.filter((node: any) => node.type === 'outputObjectNode');

            setInputNodes(inputs);
            setOutputNodes(outputs);

            if (isEditMode && flow?.library) {
              const storedInputIds = flow.library.visible_input_ids;
              const storedOutputIds = flow.library.visible_output_ids;

              const finalInputIds = Array.isArray(storedInputIds) && storedInputIds.length > 0
                ? storedInputIds
                : inputs.map((n: any) => n.id);
              const finalOutputIds = Array.isArray(storedOutputIds) && storedOutputIds.length > 0
                ? storedOutputIds
                : outputs.map((n: any) => n.id);
              
              setVisibleInputIds(finalInputIds);
              setVisibleOutputIds(finalOutputIds);
            } else {
              setVisibleInputIds(inputs.map((n: any) => n.id));
              setVisibleOutputIds(outputs.map((n: any) => n.id));
            }
          }
        } catch (error) {
          console.error('Failed to fetch flow nodes:', error);
        }
      }
    };

    fetchFlowNodes();
  }, [show, flow?.id, flow?.library, isEditMode]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type.startsWith('image/')) {
        setSelectedImage(file);
      }
    }
  }, []);

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
        setSelectedImage(e.target.files[0]);
      }
    },
    [],
  );

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleInputCheckboxChange = (nodeId: string) => {
    setVisibleInputIds(prev => {
      if (prev.includes(nodeId)) {
        const newIds = prev.filter(id => id !== nodeId);
        return newIds.length > 0 ? newIds : prev;
      }
      return [...prev, nodeId];
    });
  };

  const handleOutputCheckboxChange = (nodeId: string) => {
    setVisibleOutputIds(prev => {
      if (prev.includes(nodeId)) {
        const newIds = prev.filter(id => id !== nodeId);
        return newIds.length > 0 ? newIds : prev;
      }
      return [...prev, nodeId];
    });
  };

  const handleDelete = async () => {
    if (!isEditMode || !flow?.library?.id) return;

    if (!window.confirm('Are you sure you want to remove this flow from the library?')) {
      return;
    }

    setIsLoading(true);
    try {
      await LibraryService.deleteLibrary(flow.library.id);

      setNotification({
        type: 'success',
        message: 'Library removed successfully',
      });

      onAddToLibrary({
        libraryType,
        image: selectedImage || undefined,
      });

      setSelectedImage(null);
      onHide();
    } catch (error: any) {
      setNotification({
        type: 'error',
        message: getAPIErrorMessage(error),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdd = async () => {
    if (visibleInputIds.length === 0 || visibleOutputIds.length === 0) {
      setNotification({
        type: 'error',
        message: 'At least one data input and one data output must be visible',
      });
      return;
    }

    if (isEditMode && flow?.library?.id) {
      setIsLoading(true);
      try {
        let backendType: 'system' | 'organization' | 'space' | 'private' = 'space';
        let companyId: string | undefined = undefined;
        let spaceId: string | null | undefined = undefined;

        if (libraryType === 'global') {
          backendType = 'system';
          companyId = user?.selectedCompanyId || undefined;
        } else if (libraryType === 'environment') {
          backendType = 'organization';
          companyId = user?.selectedCompanyId || undefined;
        } else if (libraryType === 'space') {

          let flowSpaceId: string | null | undefined = null;

          if (spaceId) {
            flowSpaceId = spaceId;
          } else if (folder?.space) {
            flowSpaceId = folder.space;
          } else if (folderId && folders) {
            const findFolderById = (folderList: any[]): any => {
              for (const f of folderList) {
                if (f.id === folderId) {
                  return f;
                }
                if (f.folders) {
                  const nestedFolder = findFolderById(f.folders);
                  if (nestedFolder) return nestedFolder;
                }
              }
              return null;
            };
            const foundFolder = findFolderById(folders);
            if (foundFolder?.space) {
              flowSpaceId = foundFolder.space;
            }
          } else if (folders) {
            const findFolderWithFlow = (folderList: any[]): any => {
              for (const f of folderList) {
                if (f.flows?.some((fl: any) => fl.id === flow?.id)) {
                  return f;
                }
                if (f.folders) {
                  const nestedFolder = findFolderWithFlow(f.folders);
                  if (nestedFolder) return nestedFolder;
                }
              }
              return null;
            };
            const foundFolder = findFolderWithFlow(folders);
            if (foundFolder?.space) {
              flowSpaceId = foundFolder.space;
            }
          }
          if (!flowSpaceId) {
            flowSpaceId = flow?.spaceId || null;
          }

          if (flowSpaceId) {
            backendType = 'space';
            companyId = user?.selectedCompanyId || undefined;
            spaceId = flowSpaceId;
          } else {
            backendType = 'private';
            companyId = user?.selectedCompanyId || undefined;
            spaceId = null;
          }
        }

        const options = {
          image: selectedImage || undefined,
          type: backendType,
          company_id: companyId,
          space_id: spaceId,
          visible_input_ids: visibleInputIds,
          visible_output_ids: visibleOutputIds,
        };

        await LibraryService.updateLibrary(flow.library.id, options);

        setNotification({
          type: 'success',
          message: 'Library updated successfully',
        });

        onAddToLibrary({
          libraryType,
          image: selectedImage || undefined,
        });

        setSelectedImage(null);
        onHide();
      } catch (error: any) {
        setNotification({
          type: 'error',
          message: getAPIErrorMessage(error),
        });
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (libraryType !== 'global' && !user?.selectedCompanyId) {
      setNotification({
        type: 'error',
        message: 'Please select a company to add to library',
      });
      return;
    }

    if (!flow?.id) {
      setNotification({
        type: 'error',
        message: 'Flow ID is required to add to library',
      });
      return;
    }

    let flowSpaceId: string | null | undefined = null;
    
    if (spaceId) {
      flowSpaceId = spaceId;
    }
    else if (folder?.space) {
      flowSpaceId = folder.space;
    }
    else if (folderId && folders) {
      const findFolderById = (folderList: any[]): any => {
        for (const f of folderList) {
          if (f.id === folderId) {
            return f;
          }
          if (f.folders) {
            const nestedFolder = findFolderById(f.folders);
            if (nestedFolder) return nestedFolder;
          }
        }
        return null;
      };
      const foundFolder = findFolderById(folders);
      if (foundFolder?.space) {
        flowSpaceId = foundFolder.space;
      }
    }
    else if (folders) {
      const findFolderWithFlow = (folderList: any[]): any => {
        for (const f of folderList) {
          if (f.flows?.some((fl: any) => fl.id === flow?.id)) {
            return f;
          }
          if (f.folders) {
            const nestedFolder = findFolderWithFlow(f.folders);
            if (nestedFolder) return nestedFolder;
          }
        }
        return null;
      };
      const foundFolder = findFolderWithFlow(folders);
      if (foundFolder?.space) {
        flowSpaceId = foundFolder.space;
      }
    }
    if (!flowSpaceId) {
      flowSpaceId = flow?.spaceId || null;
    }

    setIsLoading(true);

    try {
      let response;
      
      const options = {
        image: selectedImage || undefined,
        visible_input_ids: visibleInputIds,
        visible_output_ids: visibleOutputIds,
      };

      if (libraryType === 'space') {
        const companyId = user?.selectedCompanyId;
        if (!companyId) {
          throw new Error('Company ID is required for space library');
        }
        if (!flowSpaceId) {
          response = await LibraryService.createPrivateLibrary(
            companyId,
            flow.id,
            options,
          );
        } else {
          response = await LibraryService.createSpaceLibrary(
            companyId,
            flowSpaceId,
            flow.id,
            options,
          );
        }
      } else if (libraryType === 'environment') {
        const companyId = user?.selectedCompanyId;
        if (!companyId) {
          throw new Error('Company ID is required for organization library');
        }
        response = await LibraryService.createOrganizationLibrary(
          companyId,
          flow.id,
          options,
        );
      } else if (libraryType === 'global') {
        response = await LibraryService.createSystemLibrary(
          user?.selectedCompanyId || null,
          flow.id,
          options,
        );
      } else {
        throw new Error('Invalid library type');
      }

      setNotification({
        type: 'success',
        message: 'Agent added to library successfully',
      });

      onAddToLibrary({
        libraryType,
        image: selectedImage || undefined,
      });

      setLibraryType('space');
      setSelectedImage(null);
      onHide();
    } catch (error: any) {
      setNotification({
        type: 'error',
        message: getAPIErrorMessage(error),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <KitModal
      onHide={onHide}
      show={show}
      fullWidth
      sx={{
        '& .MuiPaper-root': {
          maxWidth: '600px',
          borderRadius: '8px',
        },
        '& .MuiDialogContent-root': {
          padding: '32px',
        },
        '& .MuiDialogActions-root': {
          padding: '32px',
        },
      }}
      showCloseButton={true}
      title={
        <Box display="flex" flexDirection="column" gap="12px">
            <Box display="flex" gap="8px" alignItems="center">
                <BookOpen size={24} color={user?.theme === 'dark' ? '#D0D5DD' : '#3650DE'} />
                <Typography
                    fontSize="20px"
                    fontWeight={600}
                    color={user?.theme === 'dark' ? '#D0D5DD' : '#101828'}
                >
                    {isEditMode ? 'Edit Library' : 'Add to Library'}
                </Typography>
            </Box>
            <Typography
            sx={{
            color: user?.theme === 'dark' ? '#D0D5DD' : '#475467',
            fontSize: '14px',
            fontWeight: 400,
            }}
        >
            {isEditMode
              ? 'Update your library settings and image.'
              : 'Configure your flow settings and library assignment.'}
        </Typography>
      </Box>
      }
    >
      <Divider
        sx={{
          marginTop: '-32px',
          marginBottom: '24px',
          borderColor: user?.theme === 'dark' ? '#344054' : '#E4E7EC',
        }}
      />
      <Box display="flex" flexDirection="column" gap="24px">
        <Box display="flex" flexDirection="column" gap="12px">
            <Box display="flex" flexDirection="column" gap="4px" mb="12px">
                <Typography
                    sx={{
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    fontSize: '14px',
                    fontWeight: 600,
                    }}
                >
                    Assign this agent to
                </Typography>
                <Typography
                    sx={{
                    color: user?.theme === 'dark' ? '#98A2B3' : '#101828',
                    fontSize: '14px',
                    fontWeight: 400,
                    }}
                >
                    Choose where this agent will be accessible within your organization.
                </Typography>
          </Box>
          <RadioGroup
            value={libraryType}
            onChange={e => setLibraryType(e.target.value)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <Box display="flex" flexDirection="row" alignItems="center" gap="12px">
              <Radio
                value="space"
                sx={{
                  color: user?.theme === 'dark' ? '#D0D5DD' : '#475467',
                  padding: '4px',
                  alignSelf: 'center',
                  transform: 'scale(0.85)',
                  '&.Mui-checked': {
                    color: '#3650DE',
                  },
                }}
              />
              <Box display="flex" flexDirection="column" gap="4px" flex="1">
                <Typography
                  sx={{
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    fontSize: '14px',
                    fontWeight: 600,
                  }}
                >
                  Space Library
                </Typography>
                <Typography
                  sx={{
                    color: user?.theme === 'dark' ? '#98A2B3' : '#667085',
                    fontSize: '14px',
                  }}
                >
                  Make this Agent available inside the space it was created in.
                </Typography>
              </Box>
            </Box>

            {user?.selectedCompanyId && (
              <Box display="flex" flexDirection="row" alignItems="center" gap="12px">
                <Radio
                  value="environment"
                  sx={{
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#475467',
                    padding: '4px',
                    alignSelf: 'center',
                    transform: 'scale(0.85)',
                    '&.Mui-checked': {
                      color: '#3650DE',
                    },
                  }}
                />
                <Box display="flex" flexDirection="column" gap="4px" flex="1">
                  <Typography
                    sx={{
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                      fontSize: '14px',
                      fontWeight: 600,
                    }}
                  >
                    Organization Library
                  </Typography>
                  <Typography
                    sx={{
                      color: user?.theme === 'dark' ? '#98A2B3' : '#667085',
                      fontSize: '14px',
                    }}
                  >
                    Make this Agent available inside the organization it was created in.
                  </Typography>
                </Box>
              </Box>
            )}

            {isProductAdmin && (
              <Box display="flex" flexDirection="row" alignItems="center" gap="12px">
                <Radio
                  value="global"
                  sx={{
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#475467',
                    padding: '4px',
                    alignSelf: 'center',
                    transform: 'scale(0.85)',
                    '&.Mui-checked': {
                      color: '#3650DE',
                    },
                  }}
                />
                <Box display="flex" flexDirection="column" gap="4px" flex="1">
                  <Typography
                    sx={{
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                      fontSize: '14px',
                      fontWeight: 600,
                    }}
                  >
                    Global System Library
                  </Typography>
                  <Typography
                    sx={{
                      color: user?.theme === 'dark' ? '#98A2B3' : '#667085',
                      fontSize: '14px',
                    }}
                  >
                    Make this Agent available to the entire platform.
                  </Typography>
                </Box>
              </Box>
            )}
          </RadioGroup>
        </Box>

        {(inputNodes.length > 0 || outputNodes.length > 0) && (
          <>
            <Divider
              sx={{
                marginTop: '2px',
                marginBottom: '2px',
                borderColor: user?.theme === 'dark' ? '#344054' : '#E4E7EC',
              }}
            />

            <Box display="flex" flexDirection="column" gap="12px">
              <Box display="flex" flexDirection="column" gap="4px">
                <Typography
                  sx={{
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    fontSize: '14px',
                    fontWeight: 600,
                  }}
                >
                  Visible Data Inputs & Outputs
                </Typography>
                <Typography
                  sx={{
                    color: user?.theme === 'dark' ? '#98A2B3' : '#101828',
                    fontSize: '14px',
                    fontWeight: 400,
                  }}
                >
                  Select which data inputs and outputs will be visible when this agent is used in other flows.
                </Typography>
              </Box>

              {inputNodes.length > 0 && (
                <Box display="flex" flexDirection="column" gap="8px">
                  <Typography
                    sx={{
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                      fontSize: '13px',
                      fontWeight: 600,
                    }}
                  >
                    Data Inputs (at least 1 required)
                  </Typography>
                  <Box display="flex" flexDirection="column" gap="4px">
                    {inputNodes.map((node: any) => (
                      <FormControlLabel
                        key={node.id}
                        sx={{ marginLeft: '4px', gap: '12px' }}
                        control={
                          <Checkbox
                            checked={visibleInputIds.includes(node.id)}
                            onChange={() => handleInputCheckboxChange(node.id)}
                            sx={{
                              color: user?.theme === 'dark' ? '#D0D5DD' : '#475467',
                              padding: '4px',
                              '&.Mui-checked': {
                                color: '#3650DE',
                              },
                            }}
                          />
                        }
                        label={
                          <Typography
                            sx={{
                              color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                              fontSize: '14px',
                              marginLeft: '4px',
                            }}
                          >
                            {node.data?.label || node.data?.varName || `Input ${node.id.substring(0, 8)}`}
                          </Typography>
                        }
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {outputNodes.length > 0 && (
                <Box display="flex" flexDirection="column" gap="8px">
                  <Typography
                    sx={{
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                      fontSize: '13px',
                      fontWeight: 600,
                    }}
                  >
                    Data Outputs (at least 1 required)
                  </Typography>
                  <Box display="flex" flexDirection="column" gap="4px">
                    {outputNodes.map((node: any) => (
                      <FormControlLabel
                        key={node.id}
                        sx={{ marginLeft: '4px', gap: '12px' }}
                        control={
                          <Checkbox
                            checked={visibleOutputIds.includes(node.id)}
                            onChange={() => handleOutputCheckboxChange(node.id)}
                            sx={{
                              color: user?.theme === 'dark' ? '#D0D5DD' : '#475467',
                              padding: '4px',
                              '&.Mui-checked': {
                                color: '#3650DE',
                              },
                            }}
                          />
                        }
                        label={
                          <Typography
                            sx={{
                              color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                              fontSize: '14px',
                              marginLeft: '4px',
                            }}
                          >
                            {node.data?.label || node.data?.outputName || `Output ${node.id.substring(0, 8)}`}
                          </Typography>
                        }
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </>
        )}

        {(inputNodes.length === 0 || outputNodes.length === 0) && (
          <>
            <Divider
              sx={{
                marginTop: '2px',
                marginBottom: '2px',
                borderColor: user?.theme === 'dark' ? '#344054' : '#E4E7EC',
              }}
            />
            <Box
              sx={{
                padding: '16px',
                borderRadius: '8px',
                backgroundColor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                border: `1px solid ${user?.theme === 'dark' ? '#344054' : '#E4E7EC'}`,
              }}
            >
              <Typography
                sx={{
                  color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                  fontSize: '14px',
                  fontWeight: 400,
                }}
              >
                At least one data input and one data output are required to make a library.
              </Typography>
            </Box>
          </>
        )}

        <Divider
          sx={{
            marginTop: '2px',
            marginBottom: '2px',
            borderColor: user?.theme === 'dark' ? '#344054' : '#E4E7EC',
          }}
        />

        <Box display="flex" flexDirection="column" gap="12px">
          <Typography
            sx={{
              color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
              fontSize: '14px',
              fontWeight: 600,
            }}
          >
            Upload an Image
          </Typography>
          <Typography
            sx={{
              color: user?.theme === 'dark' ? '#98A2B3' : '#101828',
              fontSize: '14px',
              fontWeight: 400,
              mb: '12px',
            }}
          >
            Uploading an image, such as a logo or icon, helps identify the library
            and improves your workflow by creating a smoother navigation experience.
          </Typography>
          <Box
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={handleClick}
            sx={{
              border: `2px dashed ${
                dragActive
                  ? '#3650DE'
                  : user?.theme === 'dark'
                  ? '#344054'
                  : '#D0D5DD'
              }`,
              borderRadius: '8px',
              padding: '32px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              cursor: 'pointer',
              backgroundColor:
                user?.theme === 'dark'
                  ? dragActive
                    ? '#1D2939'
                    : '#101828'
                  : dragActive
                  ? '#F9FAFB'
                  : '#FFFFFF',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: '#3650DE',
                backgroundColor:
                  user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
              },
            }}
          >
            <UploadSimple
              size={24}
              color={user?.theme === 'dark' ? '#98A2B3' : '#667085'}
            />
            <Typography
              sx={{
                color: user?.theme === 'dark' ? '#D0D5DD' : '#475467',
                fontSize: '14px',
                textAlign: 'center',
              }}
            >
              Click here or drag and drop an image. Use PNG, JPEG, or SVG formats,
              ideally at a 100x100 px ratio.
            </Typography>
            {selectedImage && (
              <Typography
                sx={{
                  color: '#3650DE',
                  fontSize: '12px',
                  marginTop: '4px',
                }}
              >
                Selected: {selectedImage.name}
              </Typography>
            )}
            {!selectedImage && isEditMode && existingImageUrl && (
              <Typography
                sx={{
                  color: user?.theme === 'dark' ? '#98A2B3' : '#667085',
                  fontSize: '12px',
                  marginTop: '4px',
                }}
              >
                Current image: {existingImageUrl.split('/').pop()}
              </Typography>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/svg+xml"
              onChange={handleFileInput}
              style={{ display: 'none' }}
            />
          </Box>
        </Box>

        <Box
          display="flex"
          justifyContent="space-between"
          gap="12px"
          sx={{ marginTop: '8px' }}
        >
          <Box>
            {isEditMode && (
              <Button
                variant="outlined"
                onClick={handleDelete}
                disabled={isLoading}
                sx={{
                  borderColor: '#D92D20',
                  color: '#D92D20',
                  textTransform: 'unset',
                  '&:hover': {
                    borderColor: '#B42318',
                    backgroundColor: 'rgba(217, 45, 32, 0.04)',
                  },
                  '&:disabled': {
                    borderColor: '#98A2B3',
                    color: '#98A2B3',
                  },
                }}
                startIcon={<Trash size={16} weight="bold" />}
              >
                Delete
              </Button>
            )}
          </Box>
          <Box display="flex" gap="12px">
            <Button
              variant="outlined"
              onClick={onHide}
              sx={{
                borderColor: user?.theme === 'dark' ? '#344054' : '#D0D5DD',
                color: user?.theme === 'dark' ? '#D0D5DD' : '#475467',
                textTransform: 'unset',
                '&:hover': {
                  borderColor: user?.theme === 'dark' ? '#475467' : '#98A2B3',
                  backgroundColor: 'transparent',
                },
              }}
            >
              Cancel
            </Button>
            <Tooltip
              title={
                inputNodes.length === 0 || outputNodes.length === 0
                  ? 'At least one data input and one data output are required to make a library.'
                  : ''
              }
              arrow
              placement="top"
            >
              <span>
                <Button
                  variant="contained"
                  onClick={handleAdd}
                  disabled={isLoading || inputNodes.length === 0 || outputNodes.length === 0}
                  sx={{
                    backgroundColor: '#3650DE',
                    color: '#FFFFFF',
                    textTransform: 'unset',
                    '&:hover': {
                      backgroundColor: '#2E42B8',
                    },
                    '&:disabled': {
                      backgroundColor: '#98A2B3',
                      color: '#FFFFFF',
                    },
                  }}
                  startIcon={<Plus size={16} weight="bold" />}
                >
                  {isLoading
                    ? isEditMode
                      ? 'Updating...'
                      : 'Adding...'
                    : isEditMode
                    ? 'Update'
                    : 'Add'}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </KitModal>
  );
};

export default AddToLibraryModal;

